import * as React from 'react';
import {
  root as rootCls,
  fluid as fluidCls,
  header as headerCls,
  title as titleCls,
  subheader as subheaderCls,
  content as contentCls,
  titleGroup as titleGroupCls,
  titleIcon as titleIconCls,
} from './RailPanel.css';

export interface RailPanelProps extends React.HTMLAttributes<HTMLElement> {
  /** Title shown in the header row (e.g. "Workstreams", "Deliverables"). */
  title: string;
  /**
   * Glyph shown beside the title — pass the SAME icon as the `IconRail.Button`
   * that opens this panel. Every panel is the same box in the same slot, so the
   * echoed glyph is what tells you which one you are looking at without reading
   * the heading. Decorative: the panel is already named by `aria-label={title}`.
   */
  icon?: React.ReactNode;
  /** Optional element rendered to the right of the title (e.g. "+ New" button). */
  headerAction?: React.ReactNode;
  /**
   * Optional uppercase subheader label below the title — e.g.
   * "MY WORKSTREAMS", "DELIVERABLES", "FILES", "WORKSTREAM AGENTS".
   */
  subheaderLabel?: string;
  /**
   * Heading level for the panel title. Default `2` (`<h2>`). Set to `3` /
   * `4` when the panel is nested under an existing higher-level heading so
   * the document heading outline stays correct (a hardcoded `<h2>` under an
   * `<h2>` page section skips/duplicates a level). Purely the rendered tag —
   * visual styling is unchanged.
   */
  headingLevel?: 2 | 3 | 4;
  /** Optional element shown right-aligned in the subheader row (counts, actions). */
  subheaderAction?: React.ReactNode;
  /**
   * Fluid mode — drops the fixed 280px width + right border so the panel
   * fills its parent. Used by the compact responsive tier where the panel
   * is a full-pane view triggered from the hamburger drawer rather than
   * a slide-out next to a 48px IconRail.
   */
  fluid?: boolean;
  ref?: React.Ref<HTMLElement>;
}

/**
 * RailPanel — 280px slide-out panel anchored to the right of the
 * `<IconRail>`. The five canonical variants (Workstreams, Deliverables,
 * Files, Agents, Workstream Memory) are mutually exclusive — the
 * consuming shell renders at most one at a time, driven by the
 * currently-active `<IconRail.Button>`.
 *
 * Pass `fluid` to drop the fixed 280px width + right border — used by
 * the compact responsive tier where the panel becomes a full-pane view
 * via a hamburger drawer instead of sitting next to a 48px IconRail.
 *
 * The title renders as `<h2>` by default; pass `headingLevel={3 | 4}` when
 * the panel nests under a higher-level heading so the document outline
 * stays well-formed.
 *
 * Prototype: `http://localhost:8000/` BG Private rail panels.
 */
export function RailPanel({
  title,
  icon,
  headerAction,
  subheaderLabel,
  subheaderAction,
  children,
  fluid,
  headingLevel = 2,
  ref,
  className,
  ...rest
}: RailPanelProps) {
  const cls = [rootCls, fluid && fluidCls, className].filter(Boolean).join(' ');
  const Heading = `h${headingLevel}` as const;
  return (
    <aside ref={ref} className={cls} aria-label={title} {...rest}>
      <div className={headerCls}>
        <div className={titleGroupCls}>
          {icon && (
            <span className={titleIconCls} aria-hidden="true">
              {icon}
            </span>
          )}
          <Heading className={titleCls}>{title}</Heading>
        </div>
        {headerAction}
      </div>
      {subheaderLabel ? (
        <div className={subheaderCls}>
          <span>{subheaderLabel}</span>
          {subheaderAction}
        </div>
      ) : null}
      <div className={contentCls}>{children}</div>
    </aside>
  );
}
RailPanel.displayName = 'RailPanel';
